import { z } from 'zod/v3';
import { transformToValidFromAndValidTo } from '../../../common/utils/schema/transformers/transformValidPeriod';
import { CategoryCode } from '../CategoryCode';
import { ClassifierValidFormDataSchema } from '../validFormData/ClassifierValidFormDataSchema';
import { CategoryMutationDtoSchema } from './CategoryMutationDtoSchema';
import {
  ROW_UNIQUE_KEY_NAME,
  UUIDType,
} from '../../../common/utils/randomUniqueKey';

export const ClassifierMutationDtoSchema = z
  .union([
    transformToValidFromAndValidTo(
      ClassifierValidFormDataSchema.innerType()
        .innerType()
        .options[0].omit({
          categories: true,
          categoryGroups: true,
          combinedGroups: true,
        })
        .extend({
          categories: z.array(CategoryMutationDtoSchema),
          categoryGroups: z.array(CategoryMutationDtoSchema),
          combinedGroups: z.array(CategoryMutationDtoSchema),
        }),
    ),
    transformToValidFromAndValidTo(
      ClassifierValidFormDataSchema.innerType()
        .innerType()
        .options[1].innerType()
        .omit({ dataTypeCode: true })
        .omit({
          categories: true,
          categoryGroups: true,
          combinedGroups: true,
        })
        .extend({
          categories: z.array(CategoryMutationDtoSchema),
          categoryGroups: z.array(CategoryMutationDtoSchema),
          combinedGroups: z.array(CategoryMutationDtoSchema),
        }),
    ),
  ])
  /**
   * Hierarchy: Map parentKeys to parentCodes
   */
  .transform(
    ({
      hierarchy, // leaving it out of the rest because we do not need to send it to backend
      categories,
      categoryGroups,
      combinedGroups,
      ...rest
    }) => {
      const keyToCodeMap: {
        [key: UUIDType]: CategoryCode | string;
      } = {};

      for (const { code, [ROW_UNIQUE_KEY_NAME]: key } of [
        ...categories,
        ...categoryGroups,
        ...combinedGroups,
      ]) {
        keyToCodeMap[key] = code;
      }

      const mapKeyToCode = ({
        [ROW_UNIQUE_KEY_NAME]: key, // leaving it out of the rest because we do not need to send it to backend
        parentKey,
        ...category
      }: (typeof categories)[number]) => ({
        ...category,
        parentCode: (parentKey && keyToCodeMap[parentKey]) || null,
      });

      // Even though hierarchy is based only on categories we transform groups and
      // combined groups as well for the sake of uniform data type.
      // groups and combined groups will just have parentCode: null which backend will ignore
      return {
        ...rest,
        categories: categories.map(mapKeyToCode),
        categoryGroups: categoryGroups.map(mapKeyToCode),
        combinedGroups: combinedGroups.map(mapKeyToCode),
      };
    },
  );
